/**
 * scrapeSiteInfo — read a restaurant's website and pull out what we need to
 * set up a new location in one go (used by quickAddLocation.js):
 *   - images       (img/srcset/lazy attrs, <picture>, og:image, inline
 *                   background-image, JSON-LD image)
 *   - descriptions (meta/og description, JSON-LD description, longer <p> text)
 *   - menuCandidates (ranked by findMenuCandidates, same as quickReview)
 *   - socialLinks  (Facebook / Instagram profiles linked from the site)
 *   - reviewLinks  (Tripadvisor / Yelp pages linked from the site)
 *   - phones, bookingLinks (tel: links / JSON-LD, "Book a table" links)
 *
 * Scans the homepage plus up to MAX_ABOUT_PAGES "about"/"om oss" pages found
 * on it. Pages are fetched through the fetch-html Cloud Function, since a
 * browser can't fetch arbitrary third-party sites directly (no CORS).
 */
import { findMenuCandidates } from "./findMenuUrl";

const FETCH_HTML_ENDPOINT =
  "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/fetch-html";

const MAX_ABOUT_PAGES = 2;
const MAX_DESCRIPTIONS = 15;

const ABOUT_PATTERN =
  /\b(om[\s-]?oss|om[\s-]?restauranten|about([\s-]?us)?|our[\s-]?story|v[åa]r[\s-]?historie|historie|history|konsept|concept|who[\s-]?we[\s-]?are|hvem[\s-]?er[\s-]?vi)\b/i;

// Paragraphs that are almost never a description of the restaurant.
const JUNK_TEXT_PATTERN =
  /(cookie|informasjonskapsler|personvern|privacy|gdpr|javascript|nyhetsbrev|newsletter|copyright|©|alle rettigheter|all rights reserved)/i;

const LAZY_ATTRS = [
  "data-original",
  "data-zoom",
  "data-img-zoom",
  "data-src",
  "data-lazy",
  "data-lazy-src",
  "data-original-src",
  "data-bg",
  "data-background",
];

async function fetchHtml(url) {
  const res = await fetch(FETCH_HTML_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.error || `Fetch failed: ${res.status}`);
  }
  return { html: data.html, finalUrl: data.finalUrl || url };
}

function toAbsolute(raw, baseUrl) {
  if (!raw) return null;
  const trimmed = raw.trim().replace(/^url\((['"]?)(.*)\1\)$/, "$2");
  if (!trimmed || trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
    return null;
  }
  try {
    const u = new URL(trimmed, baseUrl);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.href;
  } catch {
    return null;
  }
}

// "url1 320w, url2 640w" → url2. Splitting on ", " (not ",") so commas inside
// CDN URLs (Cloudinary, Squarespace) don't break the parse.
function pickBestFromSrcset(srcset) {
  let best = null;
  srcset
    .split(/,\s+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((part) => {
      const [u, size] = part.split(/\s+/);
      if (!u) return;
      const m = (size || "").match(/^(\d+(?:\.\d+)?)(w|x)$/i);
      const w = m ? Number(m[1]) * (m[2].toLowerCase() === "x" ? 1000 : 1) : 0;
      if (!best || w > best.w) best = { url: u, w };
    });
  return best?.url || null;
}

function isProbablyNotAPhoto(url) {
  const lower = url.toLowerCase().split("?")[0];
  return (
    lower.endsWith(".svg") ||
    lower.endsWith(".ico") ||
    /(favicon|sprite|spinner|loader|pixel|tracking|placeholder|blank\.(gif|png))/.test(
      lower,
    )
  );
}

// Same idea as functions/scrapeImages.js: WordPress-style "-300x200" and
// "-scaled" variants of one image collapse to one entry.
function imageBaseKey(url) {
  try {
    const u = new URL(url);
    const name = u.pathname
      .split("/")
      .pop()
      .replace(/\.[^.]+$/, "")
      .replace(/-\d+x\d+$/, "")
      .replace(/-scaled$/, "");
    return u.hostname + "/" + name.toLowerCase();
  } catch {
    return url;
  }
}

function collectJsonLd(doc) {
  const nodes = [];
  doc.querySelectorAll('script[type="application/ld+json"]').forEach((s) => {
    try {
      const parsed = JSON.parse(s.textContent);
      const stack = Array.isArray(parsed) ? [...parsed] : [parsed];
      while (stack.length) {
        const n = stack.pop();
        if (!n || typeof n !== "object") continue;
        nodes.push(n);
        if (Array.isArray(n["@graph"])) stack.push(...n["@graph"]);
      }
    } catch {
      // Ignore invalid JSON-LD
    }
  });
  return nodes;
}

function extractImages(doc, baseUrl, pageLabel) {
  const found = [];
  const add = (raw, source) => {
    const url = toAbsolute(raw, baseUrl);
    if (url && !isProbablyNotAPhoto(url)) {
      found.push({ url, source, page: pageLabel });
    }
  };

  doc
    .querySelectorAll(
      'meta[property="og:image"], meta[property="og:image:url"], meta[name="twitter:image"]',
    )
    .forEach((m) => add(m.getAttribute("content"), "og:image"));

  collectJsonLd(doc).forEach((n) => {
    const imgs = Array.isArray(n.image) ? n.image : n.image ? [n.image] : [];
    imgs.forEach((img) =>
      add(typeof img === "string" ? img : img?.url, "json-ld"),
    );
  });

  doc.querySelectorAll("img, picture source").forEach((el) => {
    let raw = null;
    for (const attr of LAZY_ATTRS) {
      if (el.getAttribute(attr)) {
        raw = el.getAttribute(attr);
        break;
      }
    }
    const srcset = el.getAttribute("srcset") || el.getAttribute("data-srcset");
    if (!raw && srcset) raw = pickBestFromSrcset(srcset);
    if (!raw) raw = el.getAttribute("src");
    add(raw, "img");
  });

  doc.querySelectorAll("[style*='background']").forEach((el) => {
    const style = el.getAttribute("style") || "";
    const re = /url\((['"]?)([^'")]+)\1\)/g;
    let m;
    while ((m = re.exec(style))) add(m[2], "background");
  });

  LAZY_ATTRS.filter((a) => a.includes("bg") || a.includes("background")).forEach(
    (attr) =>
      doc
        .querySelectorAll(`[${attr}]`)
        .forEach((el) => add(el.getAttribute(attr), "background")),
  );

  return found;
}

function cleanText(t) {
  return (t || "").replace(/\s+/g, " ").trim();
}

function extractDescriptions(doc, pageLabel) {
  const found = [];
  const add = (raw, source) => {
    const text = cleanText(raw);
    if (text.length >= 40 && !JUNK_TEXT_PATTERN.test(text)) {
      found.push({ text, source, page: pageLabel });
    }
  };

  doc
    .querySelectorAll(
      'meta[name="description"], meta[property="og:description"], meta[name="twitter:description"]',
    )
    .forEach((m) => add(m.getAttribute("content"), "meta"));

  collectJsonLd(doc).forEach((n) => {
    if (typeof n.description === "string") add(n.description, "json-ld");
  });

  doc.querySelectorAll("nav, header, footer, script, style, noscript").forEach(
    (el) => el.remove(),
  );

  doc.querySelectorAll("p").forEach((p) => {
    const text = cleanText(p.textContent);
    if (text.length >= 80 && text.length <= 1500) add(text, "text");
  });

  return found;
}

// Strip #hash, query and trailing slash so /om-oss, /om-oss/ and /om-oss#top
// count as the same page.
function normalizePageUrl(url) {
  const u = new URL(url);
  return u.origin + (u.pathname.replace(/\/+$/, "") || "/");
}

function findAboutLinks(doc, baseUrl) {
  const origin = new URL(baseUrl).origin;
  const seen = new Set([normalizePageUrl(baseUrl)]);
  const links = [];

  doc.querySelectorAll("a[href]").forEach((a) => {
    const href = toAbsolute(a.getAttribute("href"), baseUrl);
    if (!href) return;
    let u;
    let clean;
    try {
      u = new URL(href);
      clean = normalizePageUrl(href);
    } catch {
      return;
    }
    if (seen.has(clean)) return;
    if (u.origin !== origin) return;
    const haystack = `${cleanText(a.textContent)} ${decodeURIComponent(u.pathname)}`;
    if (!ABOUT_PATTERN.test(haystack)) return;
    seen.add(clean);
    links.push(clean);
  });

  return links.slice(0, MAX_ABOUT_PAGES);
}

// Path segments that are never a page/profile name.
const FACEBOOK_NON_PAGES = new Set([
  "sharer", "sharer.php", "share", "share.php", "dialog", "plugins", "tr",
  "login", "login.php", "events", "groups", "watch", "photo", "photo.php",
  "photos", "story.php", "permalink.php", "hashtag", "help", "policies",
  "privacy", "business", "ads", "gaming", "marketplace", "home.php", "l.php",
]);
const INSTAGRAM_NON_PROFILES = new Set([
  "p", "reel", "reels", "tv", "explore", "stories", "accounts", "about",
  "developer", "legal", "direct", "web",
]);

// facebook.com/lysverket/ → { url, pictureId: "lysverket" }
// facebook.com/profile.php?id=123 and /pages/Name/123 → pictureId "123"
function parseFacebookUrl(u) {
  const segments = u.pathname.split("/").filter(Boolean);
  if (segments[0] === "profile.php") {
    const id = u.searchParams.get("id");
    return id ? { url: `https://www.facebook.com/profile.php?id=${id}`, pictureId: id } : null;
  }
  if (segments[0] === "pages" && segments.length >= 3) {
    const id = segments[2];
    return { url: `https://www.facebook.com/${segments.slice(0, 3).join("/")}`, pictureId: id };
  }
  const name = segments[0];
  if (!name || FACEBOOK_NON_PAGES.has(name.toLowerCase())) return null;
  return { url: `https://www.facebook.com/${name}`, pictureId: name };
}

function parseInstagramUrl(u) {
  const name = u.pathname.split("/").filter(Boolean)[0];
  if (!name || INSTAGRAM_NON_PROFILES.has(name.toLowerCase())) return null;
  return { url: `https://www.instagram.com/${name}/` };
}

// Facebook serves a page's profile picture without an access token; a page
// that doesn't exist answers 400, so a wrong guess just fails to load.
export const facebookPictureUrl = (pictureId) =>
  `https://graph.facebook.com/${encodeURIComponent(pictureId)}/picture?width=720&height=720`;

function extractSocialLinks(doc, baseUrl) {
  const hrefs = [];
  doc.querySelectorAll("a[href]").forEach((a) => hrefs.push(a.getAttribute("href")));
  collectJsonLd(doc).forEach((n) => {
    const sameAs = Array.isArray(n.sameAs) ? n.sameAs : n.sameAs ? [n.sameAs] : [];
    sameAs.forEach((s) => typeof s === "string" && hrefs.push(s));
  });

  const facebook = [];
  const instagram = [];
  hrefs.forEach((raw) => {
    const href = toAbsolute(raw, baseUrl);
    if (!href) return;
    let u;
    try {
      u = new URL(href);
    } catch {
      return;
    }
    const host = u.hostname.toLowerCase().replace(/^(www|m|web|business)\./, "");
    if (host === "facebook.com" || host === "fb.com") {
      const parsed = parseFacebookUrl(u);
      if (parsed) facebook.push(parsed);
    } else if (host === "instagram.com" || host === "instagr.am") {
      const parsed = parseInstagramUrl(u);
      if (parsed) instagram.push(parsed);
    }
  });
  return { facebook, instagram };
}

// Table-booking providers — a link to one of these is almost certainly the
// booking link. Matched against the end of the hostname.
const BOOKING_DOMAINS = [
  "opentable.com", "opentable.co.uk", "resy.com", "sevenrooms.com",
  "exploretock.com", "thefork.com", "thefork.co.uk", "lafourchette.com",
  "quandoo.com", "quandoo.no", "resdiary.com", "bookatable.com",
  "dinnerbooking.com", "easytablebooking.com", "formitable.com",
  "zenchef.com", "tablein.com", "tableonline.fi", "bordbestilling.no",
  "gastroplanner.no", "gastroplanner.se", "gastronaut.no", "tableagent.com", "bookdinners.nl",
  "eatapp.co", "tablecheck.com", "covermanager.com", "yelp.com/reservations",
  "waiteraid.com", "superb.dk", "booketbord.dk", "bookingkit.com",
  "trivec.com", "caspeco.se", "caspeco.net", "bokabord.se", "eatbu.com",
];
const BOOKING_TEXT_PATTERN =
  /\b(book|booking|reserv\w*|bestill\w*|bestilling|book bord|boka|bokning|reservieren|réserver|reservar|tafel|table)\b/i;
const BOOKING_EXCLUDE_PATTERN =
  /(facebook|instagram|tripadvisor|google\.|maps\.|mailto:|tel:|gavekort|gift|catering|jobb|career|privacy|cookie)/i;

const isBookingDomain = (u) =>
  BOOKING_DOMAINS.some((d) => {
    const [host, ...path] = d.split("/");
    const hostMatch = u.hostname === host || u.hostname.endsWith("." + host);
    return hostMatch && (!path.length || u.pathname.startsWith("/" + path.join("/")));
  });

// Phone numbers: tel: links first (most reliable), then JSON-LD telephone.
function extractPhones(doc) {
  const phones = [];
  doc.querySelectorAll('a[href^="tel:"]').forEach((a) => {
    const raw = decodeURIComponent(a.getAttribute("href").slice(4)).trim();
    if (raw.replace(/\D/g, "").length >= 6) phones.push(raw);
  });
  collectJsonLd(doc).forEach((n) => {
    if (typeof n.telephone === "string") phones.push(n.telephone.trim());
  });
  return phones;
}

function extractBookingLinks(doc, baseUrl) {
  const found = [];
  const add = (href, text, score) => {
    const url = toAbsolute(href, baseUrl);
    if (!url || BOOKING_EXCLUDE_PATTERN.test(url)) return;
    let u;
    try {
      u = new URL(url);
    } catch {
      return;
    }
    const known = isBookingDomain(u);
    found.push({
      url,
      text: cleanText(text).slice(0, 60),
      provider: known ? u.hostname.replace(/^www\./, "") : null,
      score: known ? 100 + score : score,
    });
  };

  doc.querySelectorAll("a[href]").forEach((a) => {
    const href = a.getAttribute("href");
    const text = a.textContent;
    let u;
    try {
      u = new URL(toAbsolute(href, baseUrl) || "");
    } catch {
      return;
    }
    if (isBookingDomain(u)) add(href, text, 0);
    else if (BOOKING_TEXT_PATTERN.test(cleanText(text))) add(href, text, 50);
    else if (BOOKING_TEXT_PATTERN.test(decodeURIComponent(u.pathname))) add(href, text, 30);
  });

  // Embedded booking widgets (iframes) from known providers.
  doc.querySelectorAll("iframe[src]").forEach((f) => {
    const src = f.getAttribute("src");
    try {
      if (isBookingDomain(new URL(toAbsolute(src, baseUrl) || ""))) add(src, "booking widget", 0);
    } catch {
      // ignore
    }
  });

  collectJsonLd(doc).forEach((n) => {
    if (typeof n.acceptsReservations === "string" && /^https?:/i.test(n.acceptsReservations)) {
      add(n.acceptsReservations, "acceptsReservations", 60);
    }
    const target = n.potentialAction?.target;
    const targetUrl = typeof target === "string" ? target : target?.urlTemplate;
    if (n.potentialAction?.["@type"] === "ReserveAction" && targetUrl) {
      add(targetUrl, "ReserveAction", 60);
    }
  });

  return found;
}

function extractReviewSiteLinks(doc, baseUrl) {
  const tripadvisor = [];
  const yelp = [];
  const hrefs = [];
  doc.querySelectorAll("a[href]").forEach((a) => hrefs.push(a.getAttribute("href")));
  collectJsonLd(doc).forEach((n) => {
    const sameAs = Array.isArray(n.sameAs) ? n.sameAs : n.sameAs ? [n.sameAs] : [];
    sameAs.forEach((s) => typeof s === "string" && hrefs.push(s));
  });
  hrefs.forEach((raw) => {
    const url = toAbsolute(raw, baseUrl);
    if (!url) return;
    if (/tripadvisor\.[a-z.]+\/.*-d\d+-/i.test(url)) tripadvisor.push({ url: url.split("#")[0] });
    else if (/yelp\.[a-z.]+\/biz\//i.test(url)) yelp.push({ url: url.split(/[?#]/)[0] });
  });
  return { tripadvisor, yelp };
}

const dedupeByUrl = (list) => {
  const seen = new Set();
  return list.filter((x) => {
    const key = x.url.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

function dedupeImages(images) {
  const byKey = new Map();
  images.forEach((img) => {
    const key = imageBaseKey(img.url);
    const existing = byKey.get(key);
    // Keep the first occurrence (og:image / JSON-LD are added first, and are
    // usually the site's own "hero" pick), unless a later one is the
    // un-resized original.
    if (!existing) {
      byKey.set(key, img);
    } else if (/-\d+x\d+\.[a-z]+$/i.test(existing.url) && !/-\d+x\d+\.[a-z]+$/i.test(img.url)) {
      byKey.set(key, { ...img, source: existing.source });
    }
  });
  // Logos are rarely what you want as a profile image — keep them, but last.
  const isLogo = (img) => /logo/i.test(img.url);
  return [...byKey.values()].sort((x, y) => isLogo(x) - isLogo(y));
}

function dedupeDescriptions(descriptions) {
  const out = [];
  descriptions.forEach((d) => {
    const lower = d.text.toLowerCase();
    if (out.some((o) => o.text.toLowerCase().includes(lower))) return;
    // A longer version replaces a shorter one it contains.
    const idx = out.findIndex((o) => lower.includes(o.text.toLowerCase()));
    if (idx >= 0) out[idx] = d;
    else out.push(d);
  });
  // About-page text first, then meta, then everything else.
  const rank = (d) =>
    d.page !== "home page" && d.source === "text" ? 0 : d.source === "text" ? 2 : 1;
  return out
    .sort((a, b) => rank(a) - rank(b))
    .slice(0, MAX_DESCRIPTIONS);
}

export async function scrapeSiteInfo(siteUrl) {
  const parser = new DOMParser();
  const pagesScanned = [];
  const errors = [];

  const home = await fetchHtml(siteUrl);
  const homeDoc = parser.parseFromString(home.html, "text/html");
  pagesScanned.push(home.finalUrl);

  const menuCandidates = findMenuCandidates(home.html, home.finalUrl);
  const aboutLinks = findAboutLinks(homeDoc, home.finalUrl);
  // Before extractDescriptions — it strips header/footer, where social
  // icons, phone numbers and "Book a table" buttons usually live.
  const social = extractSocialLinks(homeDoc, home.finalUrl);
  const reviewSites = extractReviewSiteLinks(homeDoc, home.finalUrl);
  const phones = extractPhones(homeDoc);
  const booking = extractBookingLinks(homeDoc, home.finalUrl);

  let images = extractImages(homeDoc, home.finalUrl, "home page");
  let descriptions = extractDescriptions(homeDoc, "home page");

  const aboutResults = await Promise.allSettled(aboutLinks.map(fetchHtml));
  aboutResults.forEach((r, i) => {
    if (r.status !== "fulfilled") {
      errors.push(`${aboutLinks[i]}: ${r.reason?.message || r.reason}`);
      return;
    }
    const { html, finalUrl } = r.value;
    // Two different links can redirect to the same page.
    if (pagesScanned.some((p) => normalizePageUrl(p) === normalizePageUrl(finalUrl))) {
      return;
    }
    const aboutDoc = parser.parseFromString(html, "text/html");
    const label = decodeURIComponent(new URL(finalUrl).pathname) || "about";
    pagesScanned.push(finalUrl);
    const aboutSocial = extractSocialLinks(aboutDoc, finalUrl);
    social.facebook.push(...aboutSocial.facebook);
    social.instagram.push(...aboutSocial.instagram);
    const aboutReviewSites = extractReviewSiteLinks(aboutDoc, finalUrl);
    reviewSites.tripadvisor.push(...aboutReviewSites.tripadvisor);
    reviewSites.yelp.push(...aboutReviewSites.yelp);
    phones.push(...extractPhones(aboutDoc));
    booking.push(...extractBookingLinks(aboutDoc, finalUrl));
    images = images.concat(extractImages(aboutDoc, finalUrl, label));
    descriptions = descriptions.concat(extractDescriptions(aboutDoc, label));
  });

  return {
    images: dedupeImages(images),
    descriptions: dedupeDescriptions(descriptions),
    menuCandidates,
    socialLinks: {
      facebook: dedupeByUrl(social.facebook),
      instagram: dedupeByUrl(social.instagram),
    },
    reviewLinks: {
      tripadvisor: dedupeByUrl(reviewSites.tripadvisor),
      yelp: dedupeByUrl(reviewSites.yelp),
    },
    // Same number written differently counts once (compare digits only).
    phones: phones.filter(
      (ph, i) =>
        phones.findIndex((x) => x.replace(/\D/g, "") === ph.replace(/\D/g, "")) === i,
    ),
    // Best first; a known booking provider beats a generic "Book" link.
    bookingLinks: dedupeByUrl(booking.sort((a, b) => b.score - a.score)).slice(0, 8),
    pagesScanned,
    errors,
  };
}

const UPLOAD_FROM_URL_ENDPOINT =
  "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/upload-image-from-url";

/** Re-upload a third-party image to Firebase Storage; returns the download URL. */
export async function uploadImageFromUrl(url) {
  const res = await fetch(UPLOAD_FROM_URL_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    throw new Error(data.error || `Upload failed: ${res.status}`);
  }
  return data.downloadURL;
}
